import {
  enableProdMode,
  APP_INITIALIZER,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import * as Sentry from '@sentry/angular';

import { environment } from './environments/environment';

import { ENVIRONMENT_INITIALIZER, ENVIRONMENT } from './app/environment-init';
import { keycloakInitializer } from './app/keycloak-init';
import { KeycloakService, KeycloakAngularModule } from 'keycloak-angular';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  RealmHeaderAppender,
  HttpErrorInterceptor,
  DATE_FORMAT,
} from '$synapse/core';
import {
  MAT_DATE_LOCALE,
  MAT_DATE_FORMATS,
  MatNativeDateModule,
} from '@angular/material/core';
import { Router } from '@angular/router';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MatDialogModule } from '@angular/material/dialog';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrModule } from 'ngx-toastr';
import { AppRoutingModule } from './app/app-routing.module';
import { CandidatesModule } from './app/modules/candidates/candidates.module';
import { InternalErrorModule } from './app/modules/internal-error/internal-error.module';
import { UnauthorizedModule } from './app/modules/unauthorized/unauthorized.module';
import { PageNotFoundModule } from './app/modules/not-found/page-not-found.module';
import { AppComponent } from './app/app.component';

Sentry.init({
  dsn: environment.settings.sentry.dsn,
  environment: environment.name,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/api\.synapse\.takima\.io/,
    /^https:\/\/api\.synapse\.takima\.cloud/,
    /^https:\/\/api\.synapse\.takima\.dev/,
  ],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      // ng
      BrowserModule,
      // mat
      MatDialogModule,
      MatAutocompleteModule,
      MatChipsModule,
      MatNativeDateModule,
      // libs
      NgbModule,
      KeycloakAngularModule,
      ToastrModule.forRoot({
        positionClass: 'toast-top-right',
        closeButton: true,
        progressBar: false,
        newestOnTop: true,
        preventDuplicates: true,
      }),
      // synapse
      AppRoutingModule,
      CandidatesModule,
      InternalErrorModule,
      UnauthorizedModule,
      PageNotFoundModule
    ),
    ...ENVIRONMENT_INITIALIZER,
    {
      provide: APP_INITIALIZER,
      useFactory: keycloakInitializer,
      multi: true,
      deps: [ENVIRONMENT, KeycloakService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RealmHeaderAppender,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'fr-FR',
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: DATE_FORMAT,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
  ],
}).catch((err) => console.error(err));
